import React, {createContext} from "react";
import {Primitive} from "@sentry/types/types/misc";
import {getOrSetUBUUID} from "shared/Utils/uuidUtils";

type ErrorReportingProviderContextType = {
    reportError: (err: unknown, data?: {level?: string; tags?: {[key: string]: string}}) => void;
    logMessage?: (message: string, level?: string) => void;
};
const initialContext: ErrorReportingProviderContextType = {reportError: console.log};
export const ErrorReportingContext =
    createContext<ErrorReportingProviderContextType>(initialContext);

type ErrorReportingProviderProps = React.PropsWithChildren<{
    adaptor: {
        captureException: (
            error: unknown,
            {
                uuid,
                tags,
                level,
            }: {
                uuid?: string;
                tags?: {
                    [key: string]: Primitive;
                };
                level?: string;
            },
        ) => void;
        captureMessage: (message: string, level: string) => void;
    };
}>;

/**
 * Exposes a method for reporting errors to 3rd party services or internal logging.
 * Typically it should be used at the top of the application tree so it catches most React
 * rendering errors when used together with the ErrorBoundary component.
 * This should be done once per application otherwise we will get duplicate errors. That
 * is why there is a context check in cDM that checks if this component already exists up the tree.
 * Uses the context API to pass the `reportError` functionality down the component tree.
 */
export function ErrorReportingProvider({children, adaptor}: ErrorReportingProviderProps) {
    const uuid = getOrSetUBUUID();
    const existingContext = React.useContext(ErrorReportingContext);

    React.useEffect(() => {
        if (existingContext !== initialContext) {
            const errorMessage =
                "ErrorReportingProvider component should be used once per application.";
            const err = new Error(errorMessage);

            if (process.env.NODE_ENV === "production") {
                console.error(errorMessage);
            }
            adaptor.captureException(err, {uuid, level: "warning"});
        }
    }, [adaptor, existingContext, uuid]);

    const reportError = (err: unknown, data?: {level?: string; tags?: {[key: string]: string}}) => {
        if (process.env.NODE_ENV === "production") {
            console.error(err);
        }
        adaptor.captureException(err, {uuid, ...data});
    };

    const logMessage = (message: string, level = "debug") => {
        if (!message) {
            return;
        }

        adaptor.captureMessage(message, level);
    };

    return (
        <ErrorReportingContext.Provider
            value={{
                reportError,
                logMessage,
            }}
        >
            {children}
        </ErrorReportingContext.Provider>
    );
}
